import {z} from "zod"
import {getLanguage} from "libs/gatsby-i18next"
import {
  AvailableCognitoIdentity,
  AvailableSocialProvider,
} from "services/auth/schemas"

export const FamilyMembershipEnum = z.enum(["admin", "standard"])
/**@typedef {z.infer<typeof FamilyMembershipEnum>} TFamilyMembership */

export const FamilyMember = z.object({
  editor: z.boolean().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  membership: FamilyMembershipEnum.optional(),
  gid: z.string().optional(),
  invitee: z.boolean().optional(),
})
/**@typedef {z.infer<typeof FamilyMember>} TFamilyMember */

export const FamilyMembers = z.array(FamilyMember).default([])
/**@typedef {z.infer<typeof FamilyMembers>} TFamilyMembers */

export const FamilyMembersResponseDTO = z.object({
  id: z.string(),
  members: FamilyMembers,
})
/**@typedef {z.infer<typeof FamilyMembers>} TFamilyMembersResponseDTO */

export const RegisteredProduct = z.object({
  product_type: z.string(),
  serial_number: z.string(),
  registration_date: z.string(),
  status: z.array(z.string()).optional().default([]),
})
/**@typedef {z.infer<typeof RegisteredProduct>} TRegisteredProduct */

export const RegisteredProductsResponse = z.object({
  list: z.array(RegisteredProduct).optional().default([]),
})
/**@typedef {z.infer<typeof RegisteredProductsResponse>} TRegisteredProductsResponse */

export const MemberGenderEnum = z.enum(["", "1", "2"]).optional()
/**@typedef {z.infer<typeof MemberGenderEnum>} TMemberGenderEnum */

export const DietTypeEnum = z.enum(["vegetarian", "omnivore"]).optional()
/**@typedef {z.infer<typeof DietTypeEnum>} TDietTypeEnum */

export const GiantRegisterFromEnum = z.string().optional()
/**@typedef {z.infer<typeof GiantRegisterFromEnum>} TGiantRegisterFrom */

// Address type definitions
export const AddressType = z.enum([
  "billing",
  "contact",
  "home",
  "shipping",
  "work",
])
/**@typedef {z.infer<typeof AddressType>} TAddressType */

// User address for responses (includes addressType)
export const UserAddress = z.object({
  addressType: AddressType,
  city: z.string().optional(),
  country: z.string(), // ISO 3166 two-digit codes
  default: z.boolean(), // define which default address, each type can have only one default address
  district: z.string().optional(),
  stateProvince: z.string().optional(),
  street: z.string(),
  zipcode: z.string().optional(),
})
/**@typedef {z.infer<typeof UserAddress>} TUserAddress */

// User address for requests (no addressType needed - determined by key)
export const UserAddressRequest = z.object({
  city: z.string().optional(),
  country: z.string(), // ISO 3166 two-digit codes
  default: z.boolean(), // define which default address, each type can have only one default address
  district: z.string().optional(),
  stateProvince: z.string().optional(),
  street: z.string(),
  zipcode: z.string().optional(),
})
/**@typedef {z.infer<typeof UserAddressRequest>} TUserAddressRequest */

export const UserAddressFormValue = UserAddressRequest.extend({
  sameAsContact: z.boolean().optional(),
})
/**@typedef {z.infer<typeof UserAddressFormValue>} TUserAddressFormValue */

// Addresses for API responses (contains UserAddress with addressType)
export const Addresses = z.object({
  billing: z.array(UserAddress).nullable().optional(),
  contact: z.array(UserAddress).nullable().optional(),
  home: z.array(UserAddress).nullable().optional(),
  shipping: z.array(UserAddress).nullable().optional(),
  work: z.array(UserAddress).nullable().optional(),
})
/**@typedef {z.infer<typeof Addresses>} TAddresses */

// Addresses for API requests (contains UserAddressRequest without addressType)
export const AddressesRequest = z.object({
  billing: z.array(UserAddressRequest).nullable().optional(),
  contact: z.array(UserAddressRequest).nullable().optional(),
  home: z.array(UserAddressRequest).nullable().optional(),
  shipping: z.array(UserAddressRequest).nullable().optional(),
  work: z.array(UserAddressRequest).nullable().optional(),
})
/**@typedef {z.infer<typeof AddressesRequest>} TAddressesRequest */

export const AddressesFormValues = z.object({
  billing: z.array(UserAddressFormValue).nullable().optional(),
  contact: z.array(UserAddressFormValue).nullable().optional(),
  home: z.array(UserAddressFormValue).nullable().optional(),
  shipping: z.array(UserAddressFormValue).nullable().optional(),
  work: z.array(UserAddressFormValue).nullable().optional(),
})
/**@typedef {z.infer<typeof AddressesFormValues>} TAddressesFormValues */

// Type for dynamic access to address data using addressType as key
/**@typedef {Partial<Record<TAddressType, Array<TUserAddressRequest> | null | undefined>>} TAddressesRequestDynamic */

export const UserAddressResponse = z.object({
  addresses: Addresses,
})
/**@typedef {z.infer<typeof UserAddressResponse>} TUserAddressResponse */

/**
 * @param {any} v
 */
const undefinedTransformer = v => (!!v ? v : undefined)

export const FamilyMemberProfileInfo = z.object({
  birthday: z.string().optional().transform(undefinedTransformer),
  firstName: z.string(),
  lastName: z.string(),
  nationalityCode: z.string().optional().transform(undefinedTransformer),
  countryCode: z.string().optional().transform(undefinedTransformer),
  cityName: z.string().optional().transform(undefinedTransformer),
  formattedAddress: z.string().optional().transform(undefinedTransformer),
  height: z.string().optional().transform(undefinedTransformer),
  weight: z.string().optional().transform(undefinedTransformer),
  gender: MemberGenderEnum.optional().transform(undefinedTransformer),
})
/** @typedef {z.infer<typeof FamilyMemberProfileInfo>} TFamilyMemberProfileInfo */

export const FamilyMemberProfile = z.object({
  gid: z.string(),
  family: FamilyMember,
  info: FamilyMemberProfileInfo.optional(),
})
/** @typedef {z.infer<typeof FamilyMemberProfile>} TFamilyMemberProfile */

const GiantExtProvider = z.object({
  providerName: AvailableCognitoIdentity.transform(p =>
    p === AvailableCognitoIdentity.enum.SignInWithApple
      ? AvailableSocialProvider.enum.Apple
      : p,
  ),
  providerId: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
})
/** @typedef {z.infer<typeof GiantExtProvider>} TGiantExtProvider */

const MeasuredUnit = z.enum(["Metric", "Imperial"])
/**@typedef {z.infer<typeof MeasuredUnit>} MeasuredUnit */

const GiantUserInfo = z
  .object({
    firstName: z.string().default(""), // User from datalake may be nameless
    lastName: z.string().default(""), // User from datalake may be nameless
    gender: MemberGenderEnum,
    birthday: z.string().optional(),
    nationalityCode: z.string().optional(),
    height: z.string().optional(),
    weight: z.string().optional(),
    cityName: z.string().optional(),
    countryCode: z.string().optional(),
    formattedAddress: z.string().optional(),
    measureUnit: MeasuredUnit.optional(),
    profileImage: z.string().optional(),
    marketingConsent: z.boolean().optional().default(false),
    dietType: DietTypeEnum,
    dietNote: z.string().max(500).optional(),
    passportNumber: z.string().max(50).optional(),
    twNationalIdNo: z.string().optional(),
  })
  .transform(info => {
    let measureUnit = info.measureUnit
    let profileImage = info.profileImage

    if (!measureUnit) {
      // if height is set but measureUnit is not, must also set the measureUnit, (in case of old data migration)
      // otherwise, if the data is updated without measureUnit, api would fail
      if (info.height) {
        const height = parseFloat(info.height) ?? 0

        if (height > 107) {
          measureUnit = MeasuredUnit.Enum.Metric
        } else {
          measureUnit = MeasuredUnit.Enum.Imperial
        }
      } else {
        measureUnit =
          getLanguage() === "zh-TW"
            ? MeasuredUnit.Enum.Metric
            : MeasuredUnit.Enum.Imperial
      }
    }

    if (profileImage) {
      // prevent browser from using cached image
      profileImage =
        profileImage.length === 0
          ? undefined
          : `${profileImage}?t=${Date.now()}`
    }

    return {
      ...info,
      profileImage,
      measureUnit,
    }
  })

export const GiantUserProfile = z.object({
  gid: z.string(),
  general: z
    .object({
      isoCountryCode: z.string().optional(),
      phoneNumber: z.string().optional(),

      /**
       * Normally, `phoneVerified` and `phoneNumberVerified` have the same meaning and should only exist as one field.
       * Currently, depending on the registration method, either `phoneVerified` or `phoneNumberVerified` may appear.
       * Once the backend confirms the cause, the redundant field will be removed.
       */
      phoneVerified: z.boolean().optional(),
      phoneNumberVerified: z.boolean().optional(),

      email: z.string().optional(),
      emailVerified: z.boolean().optional(),
    })
    .optional()
    .default({}),
  UpdateAt: z.date().optional(),
  MFAStatus: z.boolean().optional().default(false),
  info: GiantUserInfo,
  family: z
    .object({
      id: z.string().optional(),
      editor: z.boolean().optional(),
      invitee: z.boolean().optional(),
      membership: z.enum(["admin", "standard"]).optional(),
    })
    .optional()
    .default({}),
  linkExtProviders: z
    .array(GiantExtProvider)
    .optional()
    .default([])
    .transform(s => Object.fromEntries(s.map(p => [p.providerName, {...p}]))),
  policyVersions: z.array(z.string()).optional().default([]),
  registerFrom: GiantRegisterFromEnum,
})

export const PhoneCodeOptions = z
  .object({
    ALLOWED_COUNTRIES: z.record(z.string(), z.number()),
  })
  .transform(data => data.ALLOWED_COUNTRIES)
  .transform(records =>
    Object.keys(records).map(key => ({
      country: key,
      code: records[key],
    })),
  )
  .transform(phoneCodeOptions => {
    const sortedOptions = phoneCodeOptions.sort((a, b) => {
      if (a.code !== b.code) {
        return a.code - b.code // ascending
      }
      return a.country.localeCompare(b.country) // alphabetically (ascending)
    })
    return sortedOptions
  })

/**@typedef {z.infer<typeof GiantUserProfile>} TGiantUser */
/**@typedef {z.infer<typeof GiantUserInfo>} TGiantUserInfo */
/**@typedef {z.infer<typeof PhoneCodeOptions>} PhoneCodeOptions */
